/** GMAT Focus Edition — full topic catalog for the Topics tab (tutor sessions). */

export type GmatSection = "quant" | "verbal" | "di" | "mindset";

export interface GmatTopic {
  id: string;
  name: string;
  section: GmatSection;
  description: string;
  /** Optional mastery placeholder for UI (0–100). */
  mastery?: number;
  recent?: string;
}

export const GMAT_SECTION_LABELS: Record<GmatSection, string> = {
  quant: "Quantitative Reasoning",
  verbal: "Verbal Reasoning",
  di: "Data Insights",
  mindset: "Mindset & strategy",
};

export const QUANT_TOPICS: GmatTopic[] = [
  { id: "q-arithmetic", name: "Arithmetic & fractions", section: "quant", description: "Order of operations, fractions, decimals, and number sense.", mastery: 72 },
  { id: "q-number-properties", name: "Number properties", section: "quant", description: "Evens/odds, primes, divisibility, remainders, and GMAT traps.", mastery: 47, recent: "5 of 7 wrong yesterday" },
  { id: "q-exponents", name: "Exponents & roots", section: "quant", description: "Laws of exponents, radicals, and simplifying expressions.", mastery: 65 },
  { id: "q-linear-equations", name: "Linear equations", section: "quant", description: "One-variable equations, isolating x, and translating words to algebra.", mastery: 78 },
  { id: "q-quadratics", name: "Quadratic equations", section: "quant", description: "Factoring, the quadratic formula, and parabola behavior.", mastery: 58 },
  { id: "q-inequalities", name: "Inequalities", section: "quant", description: "Linear inequalities, compound ranges, and sign flips.", mastery: 61 },
  { id: "q-absolute-value", name: "Absolute value", section: "quant", description: "Distance interpretation and split-case solving.", mastery: 54 },
  { id: "q-rates-work", name: "Rates & work", section: "quant", description: "Combined rates, work problems, and rowing/stream questions.", mastery: 72 },
  { id: "q-mixtures", name: "Mixtures & ratios", section: "quant", description: "Weighted averages, alligation, and proportion setups.", mastery: 66 },
  { id: "q-percentages", name: "Percent & profit", section: "quant", description: "Percent change, successive discounts, and markup/margin.", mastery: 80 },
  { id: "q-statistics", name: "Statistics", section: "quant", description: "Mean, median, mode, range, and weighted averages.", mastery: 54 },
  { id: "q-probability", name: "Probability", section: "quant", description: "Basic probability, independent events, and counting shortcuts.", mastery: 49 },
  { id: "q-combinatorics", name: "Combinatorics", section: "quant", description: "Permutations, combinations, and when order matters.", mastery: 52 },
  { id: "q-geometry-lines", name: "Geometry — lines & angles", section: "quant", description: "Parallel lines, angle rules, and polygon basics.", mastery: 68 },
  { id: "q-geometry-triangles", name: "Geometry — triangles", section: "quant", description: "Similarity, Pythagorean triples, and special right triangles.", mastery: 71 },
  { id: "q-geometry-circles", name: "Geometry — circles", section: "quant", description: "Arcs, sectors, inscribed angles, and circle equations.", mastery: 63 },
  { id: "q-coordinate", name: "Coordinate geometry", section: "quant", description: "Slope, distance, midpoint, and line equations.", mastery: 69 },
  { id: "q-word-problems", name: "Word problems strategy", section: "quant", description: "Translating GMAT stories into equations systematically.", mastery: 79 },
  { id: "q-sequences", name: "Sequences & patterns", section: "quant", description: "Arithmetic/geometric sequences and recursive patterns.", mastery: 55 },
];

export const VERBAL_TOPICS: GmatTopic[] = [
  { id: "v-rc-main-idea", name: "RC — Main idea & purpose", section: "verbal", description: "Finding the central claim and why the passage was written.", mastery: 84 },
  { id: "v-rc-detail", name: "RC — Supporting detail", section: "verbal", description: "Locating evidence and avoiding trap answers that distort the text.", mastery: 76 },
  { id: "v-rc-inference", name: "RC — Inference", section: "verbal", description: "Drawing justified conclusions without going too far.", mastery: 58 },
  { id: "v-rc-tone", name: "RC — Tone & attitude", section: "verbal", description: "Author tone, attitude words, and subtle stance questions.", mastery: 62 },
  { id: "v-rc-structure", name: "RC — Structure & organization", section: "verbal", description: "How paragraphs function and how arguments are built.", mastery: 64 },
  { id: "v-rc-pacing", name: "RC — Pacing & note-taking", section: "verbal", description: "Reading efficiently under time pressure on the Focus exam.", mastery: 70 },
  { id: "v-cr-strengthen", name: "CR — Strengthen", section: "verbal", description: "Adding evidence that supports the conclusion — not restating it.", mastery: 62, recent: "Often picks answers that restate the conclusion" },
  { id: "v-cr-weaken", name: "CR — Weaken", section: "verbal", description: "Breaking the link between evidence and conclusion.", mastery: 67 },
  { id: "v-cr-assumption", name: "CR — Assumption", section: "verbal", description: "Hidden premises and the assumption negation test.", mastery: 59 },
  { id: "v-cr-conclusion", name: "CR — Conclusion & inference", section: "verbal", description: "Identifying what must be true vs. what is merely suggested.", mastery: 71 },
  { id: "v-cr-flaw", name: "CR — Flaw in reasoning", section: "verbal", description: "Common logical fallacies GMAT loves to test.", mastery: 71 },
  { id: "v-cr-paradox", name: "CR — Paradox & explain", section: "verbal", description: "Resolving surprising facts without contradicting the stimulus.", mastery: 49 },
  { id: "v-cr-evaluate", name: "CR — Evaluate the argument", section: "verbal", description: "What information would help judge whether the argument works.", mastery: 56 },
  { id: "v-cr-boldface", name: "CR — Boldface", section: "verbal", description: "Roles of bolded sentences in the argument structure.", mastery: 53 },
  { id: "v-cr-cause", name: "CR — Causal reasoning", section: "verbal", description: "Correlation vs. causation and alternative explanations.", mastery: 60 },
];

export const DI_TOPICS: GmatTopic[] = [
  { id: "di-ds-fundamentals", name: "DS — Fundamentals", section: "di", description: "When is data sufficient? AD/BCE and statement logic.", mastery: 55 },
  { id: "di-ds-yes-no", name: "DS — Yes / No questions", section: "di", description: "Same statement can suffice differently for yes/no vs. value.", mastery: 65 },
  { id: "di-ds-value", name: "DS — Value questions", section: "di", description: "Finding a unique numeric answer from the statements.", mastery: 58 },
  { id: "di-ds-two-var", name: "DS — Two-variable problems", section: "di", description: "Systems, C-traps, and when one statement is enough.", mastery: 43, recent: "Pattern: trap answer C" },
  { id: "di-ds-number-properties", name: "DS — Number properties", section: "di", description: "DS setups involving integers, primes, and divisibility.", mastery: 51 },
  { id: "di-ds-geometry", name: "DS — Geometry", section: "di", description: "Figures, scale, and what each statement really fixes.", mastery: 57 },
  { id: "di-msr", name: "Multi-source reasoning", section: "di", description: "Synthesizing emails, memos, and tables under time pressure.", mastery: 52 },
  { id: "di-table", name: "Table analysis", section: "di", description: "Sorting, filtering, and inferring from sortable tables.", mastery: 60 },
  { id: "di-graphics", name: "Graphics interpretation", section: "di", description: "Reading charts, trends, and dual-axis traps.", mastery: 81 },
  { id: "di-two-part", name: "Two-part analysis", section: "di", description: "Linked constraints — both columns must work together.", mastery: 70 },
  { id: "di-pacing", name: "DI pacing & section strategy", section: "di", description: "Time management across DI question types on Focus.", mastery: 66 },
  { id: "di-overlap-quant", name: "DI ↔ Quant overlap", section: "di", description: "When DI reuses quant skills and how to study both efficiently.", mastery: 62 },
];

export const MINDSET_TOPICS: GmatTopic[] = [
  { id: "m-anxiety", name: "Test-day anxiety", section: "mindset", description: "Calming routines and reframing pressure on exam day.", mastery: 75 },
  { id: "m-pacing", name: "Pacing strategy", section: "mindset", description: "When to move on, guess strategy, and section timing.", mastery: 68 },
  { id: "m-plateau", name: "Score plateau", section: "mindset", description: "Breaking through when practice scores stall.", mastery: 60 },
  { id: "m-review", name: "How to review mistakes", section: "mindset", description: "Error logs and turning wrong answers into drills.", mastery: 72 },
  { id: "m-study-plan", name: "Building a study plan", section: "mindset", description: "Weekly structure for working professionals.", mastery: 70 },
];

export const ALL_GMAT_TOPICS: GmatTopic[] = [
  ...QUANT_TOPICS,
  ...VERBAL_TOPICS,
  ...DI_TOPICS,
  ...MINDSET_TOPICS,
];

const TOPIC_BY_ID = new Map(ALL_GMAT_TOPICS.map((t) => [t.id, t]));

export function getTopicById(id: string): GmatTopic | undefined {
  return TOPIC_BY_ID.get(id);
}

export function topicsForSection(section: GmatSection): GmatTopic[] {
  return ALL_GMAT_TOPICS.filter((t) => t.section === section);
}

export function searchTopics(query: string): GmatTopic[] {
  const q = query.trim().toLowerCase();
  if (!q) return ALL_GMAT_TOPICS;
  return ALL_GMAT_TOPICS.filter(
    (t) =>
      t.name.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.id.includes(q),
  );
}

/** Opening prompt for a live tutor session (concept-explainer pipeline). */
export function buildLessonPrompt(topic: GmatTopic): string {
  return (
    `Teach me ${topic.name} from scratch for the GMAT Focus exam. ` +
    `${topic.description} ` +
    `Start with the first idea a student needs — we'll build step by step across this session.`
  );
}

export function buildCustomLessonPrompt(freeform: string): string {
  const t = freeform.trim();
  return (
    `I want a tutoring session on: ${t}. ` +
    `Teach this for the GMAT Focus exam from scratch — one step at a time, ` +
    `the way you would across the table with a student.`
  );
}

/** Maya recommendations — driven by weak mastery + recent flags. */
export function recommendedTopics(limit = 2): GmatTopic[] {
  return [...ALL_GMAT_TOPICS]
    .sort((a, b) => {
      const score = (t: GmatTopic) =>
        (t.recent ? 100 : 0) + (100 - (t.mastery ?? 50));
      return score(b) - score(a);
    })
    .slice(0, limit);
}
